/**
 * ValuesCarousel — custom element for the Values Blocks section.
 *
 * Mobile  (<750px): dot indicators sync with the visible card as the user
 *                   scrolls; dot click scrolls that card into view.
 *                   Uses getBoundingClientRect() to avoid offsetLeft bugs
 *                   (the section has position:relative, making it the offset
 *                   parent rather than the scrollable track).
 *
 * Desktop (≥750px): coverflow carousel — JS-driven --card-scale animation,
 *                   clone loop for seamless infinite scroll, prev/next arrows.
 *                   Scale range: 1.0 (active) → 0.65 (edge). transform-origin
 *                   is top center so scaling collapses cards downward, keeping
 *                   their tops visually aligned.
 *
 * The featured card (STEM) should be block 2 (middle DOM position) so the
 * carousel initialises centred on it.
 */
class ValuesCarousel extends HTMLElement {
  connectedCallback() {
    this._track     = this.querySelector('.values__track');
    this._origCards = Array.from(this._track.querySelectorAll('.values__card'));
    this._dots      = Array.from(this.querySelectorAll('.values__dot'));
    this._count     = this._origCards.length;
    this._rafId     = null;

    if (!this._track || this._count === 0) return;

    if (window.matchMedia('(min-width: 750px)').matches) {
      this._initDesktop();
    } else {
      this._initMobile();
    }
  }

  disconnectedCallback() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  /* ── Mobile ─────────────────────────────────────────────────────────────── */

  _initMobile() {
    if (!this._dots.length) return;

    this._dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this._mobileGoTo(i));
    });

    this._track.addEventListener('scroll', () => this._onMobileScroll(), { passive: true });
    this._updateDots(0);
  }

  _onMobileScroll() {
    const trackRect   = this._track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let activeIndex = 0;
    let minDist     = Infinity;

    this._origCards.forEach((card, i) => {
      const rect       = card.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const dist       = Math.abs(cardCenter - trackCenter);
      if (dist < minDist) {
        minDist     = dist;
        activeIndex = i;
      }
    });

    this._updateDots(activeIndex);
  }

  _updateDots(activeIndex) {
    this._dots.forEach((dot, i) => {
      const isActive = i === activeIndex;
      dot.classList.toggle('values__dot--active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  _mobileGoTo(index) {
    const card = this._origCards[index];
    if (!card) return;

    const trackRect   = this._track.getBoundingClientRect();
    const cardRect    = card.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    const cardCenter  = cardRect.left  + cardRect.width  / 2;

    this._track.scrollBy({ left: cardCenter - trackCenter, behavior: 'smooth' });
  }

  /* ── Desktop ────────────────────────────────────────────────────────────── */

  _initDesktop() {
    this._buildClones();
    this._allCards = Array.from(this._track.querySelectorAll('.values__card'));

    // Open on the featured card (index 1 = STEM in [Safety, STEM, Quality]).
    // The middle copy of the clone set starts at offset this._count.
    this._activeIdx = this._count + 1;

    this._bindDesktopEvents();

    requestAnimationFrame(() => {
      this._goTo(this._activeIdx, false);
      this._updateTransforms();
      this.setAttribute('data-initialized', '');
    });
  }

  _buildClones() {
    const makeClones = () =>
      this._origCards.map((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        return clone;
      });

    // Prepend in reverse so they arrive in correct left-to-right visual order
    makeClones()
      .reverse()
      .forEach((c) => this._track.prepend(c));

    makeClones().forEach((c) => this._track.append(c));
  }

  _goTo(idx, smooth) {
    const card = this._allCards[idx];
    if (!card) return;
    const target = card.offsetLeft + card.offsetWidth / 2 - this._track.offsetWidth / 2;
    if (smooth) {
      this._track.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    } else {
      this._track.scrollLeft = Math.max(0, target);
    }
  }

  _nearestIndex() {
    const viewCenter = this._track.scrollLeft + this._track.offsetWidth / 2;
    let nearest = 0;
    let minDist = Infinity;

    this._allCards.forEach((card, i) => {
      const dist = Math.abs(card.offsetLeft + card.offsetWidth / 2 - viewCenter);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });

    return nearest;
  }

  _handleLoop() {
    const idx    = this._nearestIndex();
    let   target = null;

    if (idx < this._count) {
      target = idx + this._count;
    } else if (idx >= this._count * 2) {
      target = idx - this._count;
    }

    if (target !== null) {
      this._activeIdx = target;
      this._track.style.scrollSnapType = 'none';
      this._goTo(target, false);
      requestAnimationFrame(() => {
        this._track.style.scrollSnapType = '';
      });
    } else {
      this._activeIdx = idx;
    }
  }

  _shift(dir) {
    const nearest = this._nearestIndex();
    const target  = nearest + dir;
    if (target < 0 || target >= this._allCards.length) return;
    this._activeIdx = target;
    this._goTo(target, true);
  }

  _updateTransforms() {
    const viewCenter = this._track.scrollLeft + this._track.offsetWidth / 2;
    const maxDist    = this._track.offsetWidth * 0.55;

    const SCALE_CENTER = 1.0;
    const SCALE_EDGE   = 0.65;

    this._allCards.forEach((card) => {
      const dist  = Math.abs(card.offsetLeft + card.offsetWidth / 2 - viewCenter);
      const t     = Math.min(dist / maxDist, 1);
      const scale = (SCALE_CENTER - t * (SCALE_CENTER - SCALE_EDGE)).toFixed(3);

      card.style.setProperty('--card-scale', scale);
      card.style.zIndex    = t < 0.1 ? '2' : '1';
      card.style.boxShadow = t < 0.1 ? 'var(--shadow-lg)' : '';
    });
  }

  _bindDesktopEvents() {
    this.querySelector('.values__arrow--prev')
      ?.addEventListener('click', () => this._shift(-1));
    this.querySelector('.values__arrow--next')
      ?.addEventListener('click', () => this._shift(1));

    this._track.addEventListener(
      'scroll',
      () => {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this._updateTransforms();
        });
      },
      { passive: true }
    );

    const onSettle = () => this._handleLoop();

    if ('onscrollend' in window) {
      this._track.addEventListener('scrollend', onSettle, { passive: true });
    } else {
      let timer;
      this._track.addEventListener(
        'scroll',
        () => {
          clearTimeout(timer);
          timer = setTimeout(onSettle, 200);
        },
        { passive: true }
      );
    }
  }
}

if (!customElements.get('values-carousel')) {
  customElements.define('values-carousel', ValuesCarousel);
}
